import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Phone, 
  MessageCircle, 
  MapPin, 
  Clock, 
  Syringe, 
  Stethoscope, 
  Scissors, 
  Ambulance, 
  Activity, 
  CheckCircle,
  Star,
  Heart,
  Users,
  Shield,
  Sparkles,
  Menu,
  X
} from "lucide-react";
import "./App.css";

const App = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Smooth scroll to section
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="/logo.png" 
                alt="Blue Cross Logo" 
                className="h-24 md:h-28 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button 
                onClick={() => scrollToSection('about')}
                className="text-slate-600 hover:text-[#0056D2] font-medium transition-colors"
                data-testid="nav-about-link"
              >
                About Us
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className="text-slate-600 hover:text-[#0056D2] font-medium transition-colors"
                data-testid="nav-services-link"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('testimonials')}
                className="text-slate-600 hover:text-[#0056D2] font-medium transition-colors"
                data-testid="nav-testimonials-link"
              >
                Testimonials
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="text-slate-600 hover:text-[#0056D2] font-medium transition-colors"
                data-testid="nav-contact-link"
              >
                Contact
              </button>
              <a
                href="tel:09587365365"
                className="bg-[#0056D2] hover:bg-[#0044A5] text-white rounded-full px-6 py-3 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                data-testid="nav-book-now-btn"
              >
                Book Now
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-slate-600"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-100" data-testid="mobile-menu">
              <div className="flex flex-col gap-4">
                <button 
                  onClick={() => scrollToSection('about')}
                  className="text-slate-600 hover:text-[#0056D2] font-medium text-left"
                  data-testid="mobile-nav-about-link"
                >
                  About Us
                </button>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="text-slate-600 hover:text-[#0056D2] font-medium text-left"
                  data-testid="mobile-nav-services-link"
                >
                  Services
                </button>
                <button 
                  onClick={() => scrollToSection('testimonials')}
                  className="text-slate-600 hover:text-[#0056D2] font-medium text-left"
                  data-testid="mobile-nav-testimonials-link"
                >
                  Testimonials
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="text-slate-600 hover:text-[#0056D2] font-medium text-left"
                  data-testid="mobile-nav-contact-link"
                >
                  Contact
                </button>
                <a
                  href="tel:09587365365"
                  className="bg-[#0056D2] text-white rounded-full px-6 py-3 font-semibold text-center"
                  data-testid="mobile-nav-book-now-btn"
                >
                  Book Now
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-gradient-to-br from-blue-50 via-white to-blue-50" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full mb-6" data-testid="hero-badge">
                <Sparkles className="w-4 h-4 text-[#0056D2]" />
                <span className="text-sm font-medium text-[#0056D2]">LGBTQ+ Friendly • Opens at 7 PM</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-slate-900 mb-6" data-testid="hero-heading">
                Compassionate Care for Your Pets
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-slate-600 mb-8" data-testid="hero-subheading">
                Trusted veterinary services in Jaipur. We treat your pets like family.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:09587365365"
                  className="bg-[#0056D2] hover:bg-[#0044A5] text-white rounded-full px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex items-center justify-center gap-2"
                  data-testid="hero-book-appointment-btn"
                >
                  <Phone size={20} />
                  Book Appointment
                </a>
                <button
                  onClick={() => scrollToSection('services')}
                  className="bg-white text-[#0056D2] border-2 border-[#0056D2] hover:bg-blue-50 rounded-full px-8 py-4 text-lg font-semibold transition-all duration-300"
                  data-testid="hero-view-services-btn"
                >
                  View Services
                </button>
              </div>
              <div className="flex items-center gap-6 mt-8">
                <div className="flex -space-x-2">
                  <div className="w-10 h-10 rounded-full bg-blue-200 border-2 border-white"></div>
                  <div className="w-10 h-10 rounded-full bg-blue-300 border-2 border-white"></div>
                  <div className="w-10 h-10 rounded-full bg-blue-400 border-2 border-white"></div>
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />
                    <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />
                    <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />
                    <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />
                    <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />
                  </div>
                  <p className="text-sm text-slate-600">500+ Happy Pet Parents</p>
                </div>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1509205477838-a534e43a849f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGNhdCUyMGFuZCUyMGRvZyUyMHRvZ2V0aGVyfGVufDB8fHx8MTc3MDk4NTMxNHww&ixlib=rb-4.1.0&q=85"
                  alt="Happy dog and cat together"
                  className="w-full h-auto"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent"></div>
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-900">24/7 Care</p>
                  <p className="text-sm text-slate-600">Always Here</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 md:py-32 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1625321171045-1fea4ac688e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHx2ZXRlcmluYXJpYW4lMjBleGFtaW5pbmclMjBkb2clMjBmcmllbmRseXxlbnwwfHx8fDE3NzA5ODUzMDh8MA&ixlib=rb-4.1.0&q=85"
                  alt="Veterinarian examining a dog"
                  className="w-full h-auto"
                  data-testid="about-image"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6" data-testid="about-heading">
                Trusted Pet Care in Jaipur
              </h2>
              <p className="text-lg leading-relaxed text-slate-600 mb-6" data-testid="about-description">
                At Blue Cross Pet Clinic, we understand that your pets are family. With years of experience and a deep love for animals, we provide comprehensive veterinary care in a warm, welcoming environment.
              </p>
              <p className="text-lg leading-relaxed text-slate-600 mb-8">
                Located in the heart of Chitrakoot, Jaipur, our state-of-the-art clinic offers everything from routine checkups to emergency care. We're proud to be an LGBTQ+ friendly clinic, welcoming all pet parents with open arms.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-2xl p-6">
                  <div className="text-3xl font-bold text-[#0056D2] mb-2" data-testid="about-stat-years">10+</div>
                  <p className="text-slate-600 font-medium">Years of Experience</p>
                </div>
                <div className="bg-blue-50 rounded-2xl p-6">
                  <div className="text-3xl font-bold text-[#0056D2] mb-2" data-testid="about-stat-pets">5000+</div>
                  <p className="text-slate-600 font-medium">Pets Treated</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 md:py-32 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4" data-testid="services-heading">
              Our Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-testid="services-subheading">
              Comprehensive veterinary care tailored to your pet's needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Syringe,
                title: "Vaccination",
                description: "Keep your pets protected with our comprehensive vaccination programs",
                testId: "service-vaccination"
              },
              {
                icon: Stethoscope,
                title: "Surgery",
                description: "Advanced surgical procedures performed by experienced veterinarians",
                testId: "service-surgery"
              },
              {
                icon: Scissors,
                title: "Pet Grooming",
                description: "Professional grooming services to keep your pet looking and feeling great",
                testId: "service-grooming"
              },
              {
                icon: Ambulance,
                title: "Emergency Care",
                description: "24/7 emergency services for urgent pet health situations",
                testId: "service-emergency"
              },
              {
                icon: Activity,
                title: "Health Checkups",
                description: "Regular health assessments to ensure your pet stays healthy",
                testId: "service-checkups"
              },
              {
                icon: Heart,
                title: "Dental Care",
                description: "Complete dental services for optimal oral health",
                testId: "service-dental"
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-blue-200 transition-all duration-300 group"
                data-testid={service.testId}
              >
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#0056D2] transition-colors duration-300">
                  <service.icon className="w-7 h-7 text-[#0056D2] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4" data-testid="why-choose-heading">
              Why Choose Blue Cross?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-testid="why-choose-subheading">
              Your pet deserves the best care, and here's why we're the right choice
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Stethoscope,
                title: "Experienced Vet",
                description: "Highly qualified veterinarians with years of expertise",
                testId: "feature-experienced"
              },
              {
                icon: Sparkles,
                title: "Hygienic Clinic",
                description: "State-of-the-art facilities maintained to the highest standards",
                testId: "feature-hygienic"
              },
              {
                icon: Shield,
                title: "Affordable Care",
                description: "Quality veterinary services at competitive prices",
                testId: "feature-affordable"
              },
              {
                icon: Users,
                title: "Friendly Staff",
                description: "Compassionate team dedicated to your pet's wellbeing",
                testId: "feature-friendly"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={feature.testId}
              >
                <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="w-10 h-10 text-[#0056D2]" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 md:py-32 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4" data-testid="testimonials-heading">
              What Pet Parents Say
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-testid="testimonials-subheading">
              Hear from our happy clients about their experience
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Priya Sharma",
                text: "Best vet clinic in Jaipur! Dr. and staff are so caring and professional. My dog Rocky loves visiting here.",
                rating: 5,
                testId: "testimonial-1"
              },
              {
                name: "Rahul Verma",
                text: "Excellent service and very reasonable prices. They took great care of my cat during her surgery. Highly recommend!",
                rating: 5,
                testId: "testimonial-2"
              },
              {
                name: "Anjali Patel",
                text: "The team is amazing! Very friendly and knowledgeable. They always take time to explain everything clearly.",
                rating: 5,
                testId: "testimonial-3"
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl border border-slate-100 relative"
                data-testid={testimonial.testId}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-500" fill="currentColor" />
                  ))}
                </div>
                <p className="text-slate-600 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-[#0056D2] font-bold">{testimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-600">Pet Parent</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Opening Hours Section */}
      <section className="py-20 md:py-32 bg-[#0056D2]" data-testid="opening-hours-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Clock className="w-16 h-16 text-white mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="opening-hours-heading">
              Opening Hours
            </h2>
            <p className="text-xl text-blue-100 mb-8" data-testid="opening-hours-time">
              We open at 7:00 PM daily
            </p>
            <p className="text-lg text-blue-100 mb-8">
              For emergencies, please call us anytime at{" "}
              <a href="tel:09587365365" className="font-bold text-white underline" data-testid="emergency-phone-link">
                095873 65365
              </a>
            </p>
            <a
              href="tel:09587365365"
              className="inline-flex items-center gap-2 bg-white text-[#0056D2] rounded-full px-8 py-4 text-lg font-semibold hover:bg-blue-50 transition-all duration-300 hover:scale-105"
              data-testid="opening-hours-call-btn"
            >
              <Phone size={20} />
              Call Now
            </a>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 md:py-32 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4" data-testid="contact-heading">
              Visit Us Today
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-testid="contact-subheading">
              We're here to help your pets live their healthiest, happiest lives
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="flex items-start gap-4" data-testid="contact-address">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#0056D2]" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Address</h3>
                  <p className="text-slate-600">
                    A6/380, Imperial Residency, Sarthi Marg,<br />
                    Chitrakoot, Jaipur, Rajasthan 302021
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-phone">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#0056D2]" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Phone</h3>
                  <a href="tel:09587365365" className="text-[#0056D2] hover:underline text-lg">
                    095873 65365
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-whatsapp">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">WhatsApp</h3>
                  <a
                    href="https://wa.me/919587365365?text=Hi,%20I'd%20like%20to%20book%20an%20appointment%20for%20my%20pet"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:underline text-lg"
                  >
                    Chat with us on WhatsApp
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-hours">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-[#0056D2]" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Hours</h3>
                  <p className="text-slate-600">Opens at 7:00 PM daily</p>
                  <p className="text-sm text-slate-500 mt-1">Emergency services available 24/7</p>
                </div>
              </div>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-xl h-[400px]"
              data-testid="contact-map"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3558.033679989664!2d75.7336!3d26.9025!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396db4d000000001%3A0x0!2sBlue%20Cross%20Pet%20Clinic!5e0!3m2!1sen!2sin!4v1620000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Blue Cross Pet Clinic Location"
              ></iframe>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0056D2] text-white py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="/logo.png" 
                  alt="Blue Cross Logo" 
                  className="h-24 md:h-28 w-auto"
                />
              </div>
              <p className="text-blue-100">
                Compassionate care for your beloved pets in Jaipur.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-4">Quick Links</h3>
              <div className="space-y-2">
                <button 
                  onClick={() => scrollToSection('about')}
                  className="block text-blue-100 hover:text-white transition-colors"
                >
                  About Us
                </button>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="block text-blue-100 hover:text-white transition-colors"
                >
                  Services
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="block text-blue-100 hover:text-white transition-colors"
                >
                  Contact
                </button>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-4">Contact Info</h3>
              <div className="space-y-2 text-blue-100">
                <p>A6/380, Imperial Residency</p>
                <p>Chitrakoot, Jaipur 302021</p>
                <a href="tel:09587365365" className="block hover:text-white">
                  095873 65365
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-blue-400 pt-8 text-center text-blue-100">
            <p>&copy; 2024 Blue Cross Pet Clinic. All rights reserved.</p>
            <p className="mt-2 text-sm">LGBTQ+ Friendly • Compassionate Care</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/919587365365?text=Hi,%20I'd%20like%20to%20book%20an%20appointment%20for%20my%20pet"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-2xl z-50 transition-transform hover:scale-110 flex items-center gap-2"
        data-testid="whatsapp-floating-btn"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="hidden sm:inline font-semibold">WhatsApp</span>
      </a>
    </div>
  );
};

export default App;
